import { sharePointConfig, oauthConfig, graphConfig, dataverseConfig, corsConfig, backendConfig } from '../config/config';

// Cache for access tokens (separate for Graph API and Dataverse)
let cachedToken = null;
let tokenExpiry = null;
let cachedDataverseToken = null;
let dataverseTokenExpiry = null;

// CORS proxy URL - configurable via environment variables
const CORS_PROXY = corsConfig.proxyUrl;

/**
 * Check if backend API should be used instead of CORS proxy
 * @returns {boolean} - True if backend API is configured and should be used
 */
const shouldUseBackendApi = () => {
  return backendConfig.isEnabled();
};

/**
 * Validate that required OAuth credentials are configured
 * @throws {Error} If credentials are missing
 */
const validateCredentials = () => {
  if (!oauthConfig.clientId || !oauthConfig.tenantId || !oauthConfig.clientSecret) {
    throw new Error('OAuth credentials are not configured. Please set REACT_APP_CLIENT_ID, REACT_APP_TENANT_ID, and REACT_APP_CLIENT_SECRET environment variables.');
  }
  if (!sharePointConfig.siteId || !sharePointConfig.listId) {
    throw new Error('SharePoint configuration is missing. Please set REACT_APP_SHAREPOINT_SITE_ID and REACT_APP_SHAREPOINT_LIST_ID environment variables.');
  }
};

/**
 * Validate that Dataverse configuration is set
 * @throws {Error} If Dataverse configuration is missing
 */
const validateDataverseConfig = () => {
  if (!dataverseConfig.baseUrl || !dataverseConfig.entitySet) {
    throw new Error('Dataverse configuration is missing. Please set REACT_APP_DATAVERSE_BASE_URL and REACT_APP_DATAVERSE_ENTITY_SET environment variables.');
  }
};

/**
 * Escape a string value for use in OData filters
 * Properly escapes single quotes and wraps the value in single quotes
 * @param {string} value - The value to escape
 * @returns {string} - The escaped value wrapped in single quotes
 */
const escapeODataFilterValue = (value) => {
  // Escape single quotes by doubling them (OData standard)
  const escaped = value.replace(/'/g, "''");
  return `'${escaped}'`;
};

/**
 * Get OAuth 2.0 access token using client credentials flow
 * Uses backend API if configured, otherwise uses CORS proxy to handle the token request from the browser
 * @returns {Promise<string>} - Access token for Microsoft Graph API
 */
const getAccessToken = async () => {
  // Check if we have a valid cached token
  if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
    return cachedToken;
  }

  // If backend API is enabled, use it to get the token
  if (shouldUseBackendApi()) {
    const response = await fetch(`${backendConfig.apiUrl}/api/token/graph`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to obtain access token from backend');
    }

    const data = await response.json();
    cachedToken = data.access_token;
    // Set expiry 50 minutes from now (tokens typically last 60 minutes)
    tokenExpiry = Date.now() + 50 * 60 * 1000;
    
    return cachedToken;
  }

  // Validate credentials before making the request
  validateCredentials();

  const tokenEndpoint = `https://login.microsoftonline.com/${oauthConfig.tenantId}/oauth2/v2.0/token`;
  
  const params = new URLSearchParams();
  params.append('client_id', oauthConfig.clientId);
  params.append('client_secret', oauthConfig.clientSecret);
  params.append('scope', 'https://graph.microsoft.com/.default');
  params.append('grant_type', 'client_credentials');

  // Use CORS proxy to bypass CORS restrictions
  const response = await fetch(CORS_PROXY + encodeURIComponent(tokenEndpoint), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error_description || 'Failed to obtain access token');
  }

  const data = await response.json();
  cachedToken = data.access_token;
  // Set expiry 5 minutes before actual expiry to be safe
  tokenExpiry = Date.now() + (data.expires_in - 300) * 1000;
  
  return cachedToken;
};

/**
 * Get OAuth 2.0 access token for Dataverse API using client credentials flow
 * Uses backend API if configured, otherwise uses CORS proxy to handle the token request from the browser
 * @returns {Promise<string>} - Access token for Dataverse
 */
const getDataverseAccessToken = async () => {
  // Check if we have a valid cached token
  if (cachedDataverseToken && dataverseTokenExpiry && Date.now() < dataverseTokenExpiry) {
    return cachedDataverseToken;
  }

  // If backend API is enabled, use it to get the token
  if (shouldUseBackendApi()) {
    const response = await fetch(`${backendConfig.apiUrl}/api/token/dataverse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to obtain Dataverse access token from backend');
    }

    const data = await response.json();
    cachedDataverseToken = data.access_token;
    // Set expiry 50 minutes from now (tokens typically last 60 minutes)
    dataverseTokenExpiry = Date.now() + 50 * 60 * 1000;
    
    return cachedDataverseToken;
  }

  // Validate credentials and Dataverse config before making the request
  validateCredentials();
  validateDataverseConfig();

  const tokenEndpoint = `https://login.microsoftonline.com/${oauthConfig.tenantId}/oauth2/v2.0/token`;
  
  const params = new URLSearchParams();
  params.append('client_id', oauthConfig.clientId);
  params.append('client_secret', oauthConfig.clientSecret);
  params.append('scope', dataverseConfig.scope);
  params.append('grant_type', 'client_credentials');

  // Use CORS proxy to bypass CORS restrictions
  const response = await fetch(CORS_PROXY + encodeURIComponent(tokenEndpoint), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error_description || 'Failed to obtain Dataverse access token');
  }

  const data = await response.json();
  cachedDataverseToken = data.access_token;
  // Set expiry 5 minutes before actual expiry to be safe
  dataverseTokenExpiry = Date.now() + (data.expires_in - 300) * 1000;
  
  return cachedDataverseToken;
};

/**
 * Search in Dataverse by lotto pubblico to get all product info and SchemaE values
 * Uses the configured DATAVERSE_ENTITY_SET value
 * @param {string} lottoPubblico - The lotto pubblico to search for
 * @returns {Promise<Object>} - Object with product info and SchemaE values
 * @throws {Error} If lotto pubblico is not found or API error occurs
 */
export const searchLottiDoc = async (lottoPubblico) => {
  console.log(`Searching Dataverse entity set '${dataverseConfig.entitySet}' with lotto pubblico: ${lottoPubblico}`);
  
  const entitySet = dataverseConfig.entitySet;
  
  // If backend API is enabled, use the proxy endpoint
  if (shouldUseBackendApi()) {
    const queryPath = `${entitySet}?$filter=cr2a3_lottopubblico eq ${escapeODataFilterValue(lottoPubblico)}`;
    const apiUrl = `${backendConfig.apiUrl}/api/proxy/dataverse/${queryPath}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Dataverse API Error: ${response.status}`);
    }

    const data = await response.json();
    
    // Check if we found any results
    if (!data.value || data.value.length === 0) {
      throw new Error(`Lotto pubblico '${lottoPubblico}' not found in Dataverse entity set '${entitySet}'`);
    }
    
    const result = data.value[0];
    
    // Return all product info and SchemaE values (column names are lowercase in OData response)
    return {
      codiceArticolo: result.cr2a3_codart || null,
      lotto: result.cr2a3_lottopubblico || null,
      commessa: result.cr2a3_commessa || null,
      matricola: result.cr2a3_matricola || null,
      numDocOriginale: result.cr2a3_numdocoriginale || null,
      schemaE: result.cr2a3_schemae || null,
      schemaE2: result.cr2a3_schemae2 || null,
      schemaE3: result.cr2a3_schemae3 || null,
      schemaE4: result.cr2a3_schemae4 || null,
      schemaE5: result.cr2a3_schemae5 || null,
      schemaE6: result.cr2a3_schemae6 || null,
    };
  }

  // Otherwise, use direct API call with token
  const accessToken = await getDataverseAccessToken();
  
  // Build the API URL for Dataverse query with proper OData escaping
  const apiUrl = `${dataverseConfig.baseUrl}/api/data/v9.2/${entitySet}?$filter=cr2a3_lottopubblico eq ${escapeODataFilterValue(lottoPubblico)}`;
  
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'OData-MaxVersion': '4.0',
      'OData-Version': '4.0',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Dataverse API Error: ${response.status}`);
  }

  const data = await response.json();
  
  // Check if we found any results
  if (!data.value || data.value.length === 0) {
    throw new Error(`Lotto pubblico '${lottoPubblico}' not found in Dataverse entity set '${entitySet}'`);
  }
  
  const result = data.value[0];
  
  // Return all product info and SchemaE values (column names are lowercase in OData response)
  return {
    codiceArticolo: result.cr2a3_codart || null,
    lotto: result.cr2a3_lottopubblico || null,
    commessa: result.cr2a3_commessa || null,
    matricola: result.cr2a3_matricola || null,
    numDocOriginale: result.cr2a3_numdocoriginale || null,
    schemaE: result.cr2a3_schemae || null,
    schemaE2: result.cr2a3_schemae2 || null,
    schemaE3: result.cr2a3_schemae3 || null,
    schemaE4: result.cr2a3_schemae4 || null,
    schemaE5: result.cr2a3_schemae5 || null,
    schemaE6: result.cr2a3_schemae6 || null,
  };
};

/**
 * Map SharePoint item to document object
 * @param {Object} item - SharePoint list item
 * @returns {Object} - Mapped document object
 */
const mapItemToDocument = (item) => ({
  id: item.id,
  name: item.fields?.FileLeafRef || 'Unknown',
  title: item.fields?.Title,
  status: item.fields?.StatoDocumento,
  fileType: item.fields?.TipoFile,
  size: item.fields?.FileSizeDisplay,
  linkUrl: item.fields?.Linkdocumento?.Url,
  webUrl: item.webUrl,
  filtroApi: item.fields?.Filtro_API,
  lastModified: item.fields?.Modified,
  created: item.fields?.Created,
});

/**
 * Search for documents by Title in SharePoint with pagination support
 * Queries SharePoint list items with Title field starting with the provided value
 * Follows @odata.nextLink to retrieve all paginated results
 * @param {string} title - The title prefix to search for
 * @param {Function} onProgress - Optional callback called with accumulated documents after each page
 * @returns {Promise<Array>} - Array of document objects
 */
export const searchDocumentsByNome = async (title, onProgress = null) => {
  console.log(`Searching for documents with FileLeafRef starting with: ${title}`);
  
  let allDocuments = [];
  
  // If backend API is enabled, use the proxy endpoint
  if (shouldUseBackendApi()) {
    const initialQueryPath = `sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/items?$filter=startswith(fields/FileLeafRef,${escapeODataFilterValue(title)})&$expand=fields&$top=10`;
    let nextUrl = `${backendConfig.apiUrl}/api/proxy/graph/${initialQueryPath}`;
    
    while (nextUrl) {
      console.log(`Fetching page: ${nextUrl}`);
      
      const response = await fetch(nextUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Not found - return what we have so far (or empty array)
          return allDocuments;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API Error: ${response.status}`);
      }

      const data = await response.json();
      
      // Map and add documents from this page (filter only ATTIVO status client-side)
      const pageDocuments = (data.value || [])
        .map(mapItemToDocument)
        .filter(doc => doc.status === 'ATTIVO');
      allDocuments = allDocuments.concat(pageDocuments);
      console.log(`Fetched ${pageDocuments.length} ATTIVO documents, total so far: ${allDocuments.length}`);
      
      // Call progress callback if provided (show results immediately)
      if (onProgress && pageDocuments.length > 0) {
        onProgress([...allDocuments]);
      }
      
      // Check for nextLink for pagination
      if (data['@odata.nextLink']) {
        // The nextLink is a full Graph API URL, we need to proxy it through our backend
        // Extract the path after graph.microsoft.com/v1.0/
        const nextLinkUrl = data['@odata.nextLink'];
        const graphBasePath = 'https://graph.microsoft.com/v1.0/';
        if (nextLinkUrl.startsWith(graphBasePath)) {
          const relativePath = nextLinkUrl.substring(graphBasePath.length);
          nextUrl = `${backendConfig.apiUrl}/api/proxy/graph/${relativePath}`;
        } else {
          // If it's already a relative path or different format, try using it directly
          nextUrl = `${backendConfig.apiUrl}/api/proxy/graph/${nextLinkUrl}`;
        }
      } else {
        nextUrl = null; // No more pages
      }
    }
    
    console.log(`Total documents found: ${allDocuments.length}`);
    return allDocuments;
  }

  // Otherwise, use direct API call with token
  const accessToken = await getAccessToken();
  
  // Build the initial API URL
  let nextUrl = `${graphConfig.baseUrl}/sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/items?$filter=startswith(fields/FileLeafRef,${escapeODataFilterValue(title)})&$expand=fields&$top=10`;
  
  while (nextUrl) {
    console.log(`Fetching page: ${nextUrl}`);
    
    const response = await fetch(nextUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Not found - return what we have so far (or empty array)
        return allDocuments;
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `API Error: ${response.status}`);
    }

    const data = await response.json();
    
    // Map and add documents from this page (filter only ATTIVO status client-side)
    const pageDocuments = (data.value || [])
      .map(mapItemToDocument)
      .filter(doc => doc.status === 'ATTIVO');
    allDocuments = allDocuments.concat(pageDocuments);
    console.log(`Fetched ${pageDocuments.length} ATTIVO documents, total so far: ${allDocuments.length}`);
    
    // Call progress callback if provided (show results immediately)
    if (onProgress && pageDocuments.length > 0) {
      onProgress([...allDocuments]);
    }
    
    // Check for nextLink for pagination
    nextUrl = data['@odata.nextLink'] || null;
  }
  
  console.log(`Total documents found: ${allDocuments.length}`);
  return allDocuments;
};

/**
 * Complete search flow: search by lotto pubblico and retrieve documents
 * 1. Search configured Dataverse entity set by lotto pubblico to get all product info and SchemaE values
 * 2. For each non-empty SchemaE value, search documents by Title
 * @param {string} lottoPubblico - The lotto pubblico to search for
 * @param {Function} onProgress - Optional callback called with partial results as they arrive
 * @returns {Promise<Object>} - Object with product info and documents
 */
export const searchByLottoPubblico = async (lottoPubblico, onProgress = null) => {
  console.log(`Starting complete search for lotto pubblico: ${lottoPubblico}`);
  
  // Step 1: Search configured Dataverse entity set by lotto pubblico to get all data
  const lottiDocInfo = await searchLottiDoc(lottoPubblico);
  console.log(`Found Dataverse info:`, lottiDocInfo);
  
  // Step 2: Collect all non-empty SchemaE values
  const schemaEValues = [
    lottiDocInfo.schemaE,
    lottiDocInfo.schemaE2,
    lottiDocInfo.schemaE3,
    lottiDocInfo.schemaE4,
    lottiDocInfo.schemaE5,
    lottiDocInfo.schemaE6,
  ].filter(value => value !== null && value !== undefined && value !== '');
  
  console.log(`Non-empty SchemaE values:`, schemaEValues);
  
  // Step 4: Search documents by Nome for each SchemaE value
  const allDocuments = [];
  
  // Helper to remove duplicates
  const getUniqueDocuments = (docs) => docs.filter((doc, index, self) =>
    index === self.findIndex((d) => d.id === doc.id)
  );

  // Build product info once (immutable across iterations)
  const productInfo = {
    codiceArticolo: lottiDocInfo.codiceArticolo,
    lotto: lottiDocInfo.lotto,
    commessa: lottiDocInfo.commessa,
    matricola: lottiDocInfo.matricola,
    numDocOriginale: lottiDocInfo.numDocOriginale,
    schemaEValues: {
      schemaE: lottiDocInfo.schemaE,
      schemaE2: lottiDocInfo.schemaE2,
      schemaE3: lottiDocInfo.schemaE3,
      schemaE4: lottiDocInfo.schemaE4,
      schemaE5: lottiDocInfo.schemaE5,
      schemaE6: lottiDocInfo.schemaE6,
    },
  };

  // Progress callback defined outside the loop to avoid unsafe closure references
  const createProgressCallback = () => (pageDocuments) => {
    const merged = allDocuments.concat(pageDocuments);
    const unique = getUniqueDocuments(merged);
    if (onProgress) {
      onProgress({
        productInfo,
        documents: unique,
        isPartial: true,
      });
    }
  };

  const progressCallback = createProgressCallback();

  // Emit Dataverse data immediately so UI can show product info while documents are still loading
  if (onProgress) {
    onProgress({
      productInfo,
      documents: [],
      isPartial: true,
    });
  }

  for (const schemaEValue of schemaEValues) {
    try {
      const documents = await searchDocumentsByNome(schemaEValue, progressCallback);
      allDocuments.push(...documents);
    } catch (error) {
      console.warn(`Error searching documents for SchemaE value '${schemaEValue}':`, error.message);
    }
  }
  
  // Remove duplicates based on document id
  const uniqueDocuments = getUniqueDocuments(allDocuments);
  
  console.log(`Found ${uniqueDocuments.length} unique documents`);
  
  return {
    productInfo,
    documents: uniqueDocuments,
  };
};

/**
 * Search for documents by article code in SharePoint
 * Queries SharePoint list items with Filtro_API field matching the article code
 * @param {string} articleCode - The article code to search for (e.g., 'CL.3336.NZ')
 * @returns {Promise<Array>} - Array of document objects
 */
export const searchDocuments = async (articleCode) => {
  console.log(`Searching for documents with article code: ${articleCode}`);
  
  // If backend API is enabled, use the proxy endpoint
  if (shouldUseBackendApi()) {
    const queryPath = `sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/items?$filter=fields/Filtro_API eq ${escapeODataFilterValue(articleCode)}&$expand=fields`;
    const apiUrl = `${backendConfig.apiUrl}/api/proxy/graph/${queryPath}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Not found - return empty array
        return [];
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API Error: ${response.status}`);
    }

    const data = await response.json();
    
    // Map the response to document objects with proper field mapping
    const documents = (data.value || []).map((item) => ({
      id: item.id,
      name: item.fields?.FileLeafRef || 'Unknown',
      status: item.fields?.StatoDocumento,
      fileType: item.fields?.TipoFile,
      size: item.fields?.FileSizeDisplay,
      linkUrl: item.fields?.Linkdocumento?.Url,
      webUrl: item.webUrl,
      filtroApi: item.fields?.Filtro_API,
      lastModified: item.fields?.Modified,
      created: item.fields?.Created,
    }));
    
    return documents;
  }

  // Otherwise, use direct API call with token
  const accessToken = await getAccessToken();
  
  // Build the API URL to query list items by Filtro_API field with proper OData escaping
  // Format: /sites/{siteId}/lists/{listId}/items?$filter=fields/Filtro_API eq '{articleCode}'&$expand=fields
  const apiUrl = `${graphConfig.baseUrl}/sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/items?$filter=fields/Filtro_API eq ${escapeODataFilterValue(articleCode)}&$expand=fields`;
  
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      // Not found - return empty array
      return [];
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `API Error: ${response.status}`);
  }

  const data = await response.json();
  
  // Map the response to document objects with proper field mapping
  const documents = (data.value || []).map((item) => ({
    id: item.id,
    name: item.fields?.FileLeafRef || 'Unknown',
    status: item.fields?.StatoDocumento,
    fileType: item.fields?.TipoFile,
    size: item.fields?.FileSizeDisplay,
    linkUrl: item.fields?.Linkdocumento?.Url,
    webUrl: item.webUrl,
    filtroApi: item.fields?.Filtro_API,
    lastModified: item.fields?.Modified,
    created: item.fields?.Created,
  }));
  
  return documents;
};

/**
 * Download a document from SharePoint using the filename
 * Uses two-step approach: first get metadata with downloadUrl, then download from that URL
 * @param {Object} document - The document object with name (FileLeafRef)
 * @returns {Promise<Blob>} - The document as a blob
 */
export const downloadDocument = async (document) => {
  if (!document.name) {
    throw new Error('Document name not available');
  }
  
  // If backend API is enabled, use the proxy endpoint
  if (shouldUseBackendApi()) {
    // Step 1: Get file metadata (without /content) to get the downloadUrl
    const metadataPath = `sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/drive/root:/${encodeURIComponent(document.name)}:/`;
    const metadataUrl = `${backendConfig.apiUrl}/api/proxy/graph/${metadataPath}`;
    
    const metadataResponse = await fetch(metadataUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!metadataResponse.ok) {
      throw new Error(`Failed to get document metadata: ${metadataResponse.status}`);
    }
    
    const metadata = await metadataResponse.json();
    
    if (!metadata || typeof metadata !== 'object') {
      throw new Error('Invalid metadata response from server');
    }
    
    const downloadUrl = metadata['@microsoft.graph.downloadUrl'];
    
    if (!downloadUrl) {
      throw new Error('Download URL not available in document metadata');
    }
    
    // Step 2: Download directly from the pre-authenticated URL
    const downloadResponse = await fetch(downloadUrl);
    
    if (!downloadResponse.ok) {
      throw new Error(`Failed to download document: ${downloadResponse.status}`);
    }
    
    return await downloadResponse.blob();
  }

  // For direct API calls (not using backend proxy)
  const accessToken = await getAccessToken();
  
  // Step 1: Get file metadata to obtain the downloadUrl
  const metadataUrl = `${graphConfig.baseUrl}/sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/drive/root:/${encodeURIComponent(document.name)}:/`;
  
  const metadataResponse = await fetch(metadataUrl, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!metadataResponse.ok) {
    throw new Error(`Failed to get document metadata: ${metadataResponse.status}`);
  }
  
  const metadata = await metadataResponse.json();
  
  if (!metadata || typeof metadata !== 'object') {
    throw new Error('Invalid metadata response from server');
  }
  
  const downloadUrl = metadata['@microsoft.graph.downloadUrl'];
  
  if (!downloadUrl) {
    throw new Error('Download URL not available in document metadata');
  }
  
  // Step 2: Download directly from the pre-authenticated URL
  // This URL doesn't require authentication and has no CORS issues
  const downloadResponse = await fetch(downloadUrl);
  
  if (!downloadResponse.ok) {
    throw new Error(`Failed to download document: ${downloadResponse.status}`);
  }
  
  return await downloadResponse.blob();
};

/**
 * Get document metadata
 * @param {string} documentId - The document ID
 * @param {string} driveId - The drive ID (optional)
 * @returns {Promise<Object>} - The document metadata
 */
export const getDocumentMetadata = async (documentId, driveId) => {
  // If backend API is enabled, use the proxy endpoint
  if (shouldUseBackendApi()) {
    const queryPath = `sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/drive/items/${documentId}`;
    const apiUrl = `${backendConfig.apiUrl}/api/proxy/graph/${queryPath}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Document information is currently unavailable');
    }
    
    return await response.json();
  }

  // Otherwise, use direct API call with token
  const accessToken = await getAccessToken();
  
  const apiUrl = `${graphConfig.baseUrl}/sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/drive/items/${documentId}`;
  
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Document information is currently unavailable');
  }
  
  return await response.json();
};

/**
 * List documents in a specific SharePoint folder
 * @param {string} folderId - The folder ID (optional, uses root if not provided)
 * @returns {Promise<Array>} - Array of document objects
 */
export const listDocuments = async (folderId = null) => {
  // If backend API is enabled, use the proxy endpoint
  if (shouldUseBackendApi()) {
    let queryPath;
    if (folderId) {
      queryPath = `sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/drive/items/${folderId}/children`;
    } else {
      queryPath = `sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/drive/root/children`;
    }
    const apiUrl = `${backendConfig.apiUrl}/api/proxy/graph/${queryPath}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    
    return (data.value || []).map((item) => ({
      id: item.id,
      name: item.name,
      size: item.size,
      lastModifiedDateTime: item.lastModifiedDateTime,
      webUrl: item.webUrl,
      downloadUrl: item['@microsoft.graph.downloadUrl'],
      driveId: item.parentReference?.driveId,
    }));
  }

  // Otherwise, use direct API call with token
  const accessToken = await getAccessToken();
  
  let apiUrl;
  if (folderId) {
    apiUrl = `${graphConfig.baseUrl}/sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/drive/items/${folderId}/children`;
  } else {
    apiUrl = `${graphConfig.baseUrl}/sites/${sharePointConfig.siteId}/lists/${sharePointConfig.listId}/drive/root/children`;
  }
  
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    return [];
  }
  
  const data = await response.json();
  
  return (data.value || []).map((item) => ({
    id: item.id,
    name: item.name,
    size: item.size,
    lastModifiedDateTime: item.lastModifiedDateTime,
    webUrl: item.webUrl,
    downloadUrl: item['@microsoft.graph.downloadUrl'],
    driveId: item.parentReference?.driveId,
  }));
};

// Export config for reference
export { sharePointConfig };
